import { Injectable } from '@angular/core';
import {
  TranslateCompiler,
  type InterpolatableTranslation,
  type InterpolatableTranslationObject,
  type TranslationObject,
} from '@ngx-translate/core';

type TranslationValue = TranslationObject[string];

const ICU_PLURAL_PATTERN = /^\{([^,\s]+),\s*plural,\s*(.*)\}$/s;
const PLURAL_CASE_PATTERN = /(=\d+|zero|one|two|few|many|other)\s*\{/g;

@Injectable()
export class LegacySafeMessageFormatCompiler extends TranslateCompiler {
  compile(value: string, lang: string): InterpolatableTranslation {
    const pluralMessage = parsePluralMessage(value);
    if (!pluralMessage) {
      return value;
    }

    return (params?: Record<string, unknown> | unknown[]) => {
      if (!params || Array.isArray(params)) {
        return value;
      }

      const count = Number(params[pluralMessage.variable]);
      if (!Number.isFinite(count)) {
        return value;
      }

      const exactCase = pluralMessage.cases.get(`=${count}`);
      const category = new Intl.PluralRules(lang).select(count);
      const template = exactCase ?? pluralMessage.cases.get(category) ?? pluralMessage.cases.get('other') ?? value;
      return template.replaceAll('#', String(count));
    };
  }

  compileTranslations(translations: TranslationObject, lang: string): InterpolatableTranslationObject {
    const result: InterpolatableTranslationObject = {};

    for (const [key, value] of Object.entries(translations)) {
      result[key] = this.compileTranslationValue(value, lang);
    }

    return result;
  }

  private compileTranslationValue(value: TranslationValue, lang: string): InterpolatableTranslation {
    if (typeof value === 'string') {
      return this.compile(value, lang);
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.compileTranslationValue(item, lang));
    }

    if (isTranslationObject(value)) {
      return this.compileTranslations(value, lang);
    }

    return value;
  }
}

function isTranslationObject(value: TranslationValue): value is TranslationObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parsePluralMessage(value: string): { variable: string; cases: Map<string, string> } | null {
  const match = value.match(ICU_PLURAL_PATTERN);
  if (!match) {
    return null;
  }

  const [, variable, casesSource] = match;
  const cases = new Map<string, string>();
  const caseMatches = [...casesSource.matchAll(PLURAL_CASE_PATTERN)];

  for (let index = 0; index < caseMatches.length; index += 1) {
    const current = caseMatches[index];
    const next = caseMatches[index + 1];
    const key = current[1];
    const contentStart = current.index! + current[0].length;
    const contentEnd = next ? next.index! : casesSource.length;
    const content = casesSource.slice(contentStart, contentEnd).trim();

    if (!content.endsWith('}')) {
      return null;
    }

    cases.set(key, content.slice(0, -1));
  }

  if (!cases.has('other')) {
    return null;
  }

  return { variable, cases };
}
